import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, UserPlus } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ApiError, authApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { describePasswordPolicy } from "../lib/passwordPolicy";

export const Route = createFileRoute("/invite/$token")({
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const setMe = useAuthStore((s) => s.setMe);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const me = await authApi.acceptInvite(token, password, name || undefined);
      setMe(me);
      navigate({ to: "/" });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : "Could not reach the server. Check your connection.";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-5 rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-6 shadow-sm"
        noValidate
      >
        <header className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Welcome aboard</h1>
          <p className="text-sm opacity-60">Set a password to activate your account.</p>
        </header>

        <div className="space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block opacity-80">Name (optional)</span>
            <input
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
              placeholder="Your name"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block opacity-80">Password</span>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            />
            <span className="mt-1 block text-xs opacity-60">{describePasswordPolicy()}</span>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block opacity-80">Confirm password</span>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            />
          </label>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Activating…
            </>
          ) : (
            <>
              <UserPlus className="size-4" aria-hidden />
              Create account
            </>
          )}
        </button>
      </form>
    </div>
  );
}
